import React, { FC, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import VerifyPasswordModal from "../../../components/alarm-viewer/VerifyPasswordModal";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import { useDialog } from "../../../components/dialog/DialogContext";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import { CUSTOM_VALUES, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../../../constants/command-status";
import AlarmsViewerTab from "../../../containers/alarm-viewer-tab";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { Button } from "../../../components/button/Button";
import { useIcaWebsocketContext } from "../../../contexts/websocket/IcaWebsocketContext";
import PauseIcon from '../../../images/PauseIcon.svg';
import PlayIcon from '../../../images/PlayIcon.svg';
import { Category } from "../../../models/Category";
import { acknowledgeAlarm } from "../../../services/transactionService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";

const Alarms: FC = () => {
    const { translate } = useLocale();
    const [selected, setSelected] = useState(0);
    const [busy, setBusy] = useState(false);
    const dialog = useDialog();
    const history = useHistory();
    const [categories, setCategories] = useState<Category[]>([{
        displayName: translate.byKey(TranslationKeys.alarms),
        heading: translate.byKey(TranslationKeys.alarms),
        value: "",
        id: 0,
        count: 0,
    }]);
    const [selectedAlarmIds, setSelectedAlarmIds] = useState<string[]>([]);
    const [broadcastAlarm, setBroadcastAlarm] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const icaWebSocket = useIcaWebsocketContext();
    const [acknowledgedAlarmIds, setAcknowledgedAlarmIds] = useState<Set<string>>(new Set());
    const isAlarmAcknowledged = (alarmId: string): boolean => {
        return acknowledgedAlarmIds.has(alarmId);
    };

    const onBroadcastChange = () => {
        setBroadcastAlarm(!broadcastAlarm);

        if (!broadcastAlarm) { // Stop monitoring
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP, CUSTOM_VALUES.ALARMS_ONLY);
        } else { // Start monitoring again
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START, CUSTOM_VALUES.NO_REFRESH);
        }
    };

    const handleAcknowledgeClick = () => {
        const alreadyAcknowledgedAlarms = selectedAlarmIds.filter(id => isAlarmAcknowledged(id));

        if (alreadyAcknowledgedAlarms.length > 0) {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: translate.byKey(TranslationKeys.alarmsAlreadyAcknowledged)
            });
            return;
        }

        setIsPasswordModalOpen(true);
    };

    const acknowledgeAlarms = async (password: string) => {
        try {
            setBusy(true);
            const result = await acknowledgeAlarm(selectedAlarmIds, password);

            if (result.success) {
                toast.success(translate.byKey(TranslationKeys.acknowledgeAlarmSuccessfully));

                setAcknowledgedAlarmIds(prev => {
                    const newSelectedAlarms = new Set(prev);
                    selectedAlarmIds.forEach(id => newSelectedAlarms.add(id));
                    return newSelectedAlarms;
                });

                setSelectedAlarmIds([]);
            }
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <ActivityFrame busy={busy} className="flex flex-grow overflow-y-hidden">
            <div id="alarms" className="pt-4 pl-4 flex flex-col flex-grow overflow-y-hidden">
                <Breadcrumb
                    path={[
                        { value: "/access-control/monitoring", displayName: translate.byKey(TranslationKeys.monitoring) },
                        { value: "/access-control/monitoring/alarms", displayName: translate.byKey(TranslationKeys.alarms) }
                    ]}
                    onClick={(item) => history.push(item.value)}/>
                <Tabs
                    className="bg-white overflow-y-hidden"
                    tabs={getTabsListFromLabels(categories)}
                    onSelect={(i) => setSelected(i)}
                    selected={selected}
                    onAdd={() => { }}
                    add={false}
                    tabStyle={{}}>
                    <AlarmsViewerTab
                        categories={categories}
                        setCategories={setCategories}
                        selectedIds={selectedAlarmIds}
                        setSelectedIds={setSelectedAlarmIds}
                        setAcknowledgedAlarmIds={setAcknowledgedAlarmIds}/>
                    <div className="mb-viewport" />
                </Tabs>
                <div className="flex flex-row justify-end items-center flex-shrink-0 h-16 pr-8 border-t border-gray-200 bg-white">
                    <Button
                        variant="primary"
                        shape="rounded"
                        disabled={selectedAlarmIds.length === 0}
                        className="py-2 px-4"
                        onClick={handleAcknowledgeClick}>
                        <span className="mr-2">{translate.byKey(TranslationKeys.acknowledge)}</span>
                    </Button>
                    <Button
                        variant="submit"
                        shape="rounded"
                        active={broadcastAlarm}
                        className="ml-4"
                        onClick={onBroadcastChange}
                        id="broatcastAlarmBtn">
                        <img
                            src={broadcastAlarm ? PlayIcon : PauseIcon}
                            className={`px-2 ${broadcastAlarm ? '' : 'w-6 py-1'}`}
                            alt="Pause or Play transaction"/>
                        <span className="mr-2">{broadcastAlarm ? translate.byKey(TranslationKeys.play) : translate.byKey(TranslationKeys.pause)}</span>
                    </Button>
                </div>
            </div>
            <VerifyPasswordModal
                isOpen={isPasswordModalOpen}
                setIsOpen={setIsPasswordModalOpen}
                onVerify={acknowledgeAlarms}/>
        </ActivityFrame>
    );
};

export default Alarms;